/**
 * The bot enemy mesh — same capsule + facing-wedge silhouette as the player,
 * tinted red, with a floating health bar. Purely visual; the sim drives it.
 */
import { TransformNode } from "@babylonjs/core/Meshes/transformNode.js";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { Color3 } from "@babylonjs/core/Maths/math.color.js";
import type { Scene } from "@babylonjs/core/scene.js";
import { createHealthBar, type HealthBar } from "./healthBar.js";

export interface Bot {
  readonly root: TransformNode;
  setGroundPosition(x: number, z: number): void;
  setFacing(angleRad: number): void;
  setHealth(frac: number): void;
  dispose(): void;
}

export const createBot = (scene: Scene): Bot => {
  const root = new TransformNode("bot", scene);

  const body = MeshBuilder.CreateCapsule(
    "bot-body",
    { height: 1.5, radius: 0.45, tessellation: 12, subdivisions: 1 },
    scene,
  );
  body.position.y = 0.75;
  body.parent = root;
  const bodyMat = new StandardMaterial("botMat", scene);
  bodyMat.diffuseColor = new Color3(0.95, 0.35, 0.32);
  bodyMat.specularColor = new Color3(0.12, 0.12, 0.12);
  body.material = bodyMat;

  const wedge = MeshBuilder.CreateCylinder(
    "bot-facing",
    { diameterTop: 0, diameterBottom: 0.34, height: 0.5, tessellation: 12 },
    scene,
  );
  wedge.rotation.x = Math.PI / 2;
  wedge.position.set(0, 0.9, 0.55);
  wedge.parent = root;
  const wedgeMat = new StandardMaterial("botFaceMat", scene);
  wedgeMat.diffuseColor = new Color3(1, 0.7, 0.6);
  wedgeMat.emissiveColor = new Color3(0.35, 0.08, 0.06);
  wedge.material = wedgeMat;

  const bar: HealthBar = createHealthBar(scene, root);

  return {
    root,
    setGroundPosition: (x: number, z: number): void => {
      root.position.x = x;
      root.position.z = z;
    },
    setFacing: (angleRad: number): void => {
      root.rotation.y = angleRad;
    },
    setHealth: (frac: number): void => bar.set(frac),
    dispose: (): void => root.dispose(false, true),
  };
};
